"use client"
import { useRef } from "react"

// Define colors using standard Tailwind arbitrary value format for readability
const PRIMARY = "#004085" // Deep Blue
const ACCENT = "#f4511e" // Orange
const TEXT_DARK = "#1f2937" // Dark Gray
const TEXT_LIGHT = "#ffffff" // White
const GRAY_ACCENT = "#9ca3af" // Light gray for borders/accents

function getInstituteMeta(student) {
  return {
    name: student?.department_name || student?.department?.name || "INSTITUTE NAME",
    address:
      student?.department?.address || `${student?.district || ""} ${student?.state || ""}`.trim() || "Institute Address",
  }
}

export default function StudentIDCard({ student, onClose }) {
  const cardRef = useRef(null)
  const { name, address } = getInstituteMeta(student)

  const enrollmentNo = student?.enrollment_number || "—"
  const trade = student?.trade || "—"
  const studentName = student?.name || "—"

  const handlePrint = () => {
    if (!cardRef.current) {
      console.error("Card reference not found")
      alert("Unable to print ID card. Please try again.")
      return
    }
    window.print()
  }

  return (
   <>
      {enrollmentNo === "—" ? (
        <div className="w-full h-full flex flex-col items-center p-4">
          <div className="pt-10 text-gray-800 font-semibold mb-4">Enrollment number is not generated yet</div>
          {onClose && (
            <div className="print-hidden flex items-center gap-3 mb-4 p-3 bg-white rounded-lg shadow-sm">
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition duration-150 ease-in-out"
              >
                Close
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center p-4">
          {/* Controls (hidden in print view) */}
          <div className="print-hidden flex items-center gap-3 mb-4 p-3 bg-white rounded-lg shadow-sm">
            <button
              onClick={handlePrint}
              className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-150 ease-in-out"
            >
              🖨️ Print ID Card
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition duration-150 ease-in-out"
              >
                Close
              </button>
            )}
          </div>

          {/* Card Content */}
          <div ref={cardRef} className="id-card-container">
            <div
              className="shadow-xl border border-gray-300 rounded-lg overflow-hidden text-gray-800 font-sans"
              style={{ width: '54mm', height: '85.6mm', color: TEXT_DARK }}
            >
              {/* TOP SECTION (Header) */}
              <div
                className="text-center text-white px-2 py-3 print:bg-[#004085]"
                style={{ backgroundColor: PRIMARY, color: TEXT_LIGHT }}
              >
                <h1 className="m-0 text-sm font-extrabold tracking-wide leading-tight">
                  {name}
                </h1>
                <p className="m-0 mt-0.5 text-[9px] font-medium opacity-90">
                  {address}
                </p>
              </div>

              {/* IDENTITY CARD BAND */}
              <div
                className="text-center text-white py-1 font-bold tracking-wider text-[9px] print:bg-[#f4511e]"
                style={{ backgroundColor: ACCENT }}
              >
                IDENTITY CARD
              </div>

              {/* MAIN DATA SECTION */}
              <div className="bg-white px-2 py-4 flex flex-col items-center">
                <div
                  className="w-[70px] h-[90px] mb-3 border-[2px] rounded-sm bg-gray-100 flex items-center justify-center overflow-hidden text-[10px] font-semibold text-gray-600"
                  style={{ borderColor: PRIMARY }}
                >
                  {student?.profile_photo_path ? (
                    <img
                      src={`http://localhost:3000/${student.profile_photo_path}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = "/professional-headshot.png"
                      }}
                    />
                  ) : (
                    <span>Profile Photo</span>
                  )}
                </div>

                {/* Student Details */}
                <div className="w-full space-y-1.5 text-[10px] leading-normal">
                  <div className="flex items-center">
                    <span className="font-semibold w-[80px] shrink-0">Name:</span>
                    <span className="font-bold border-b flex-1 py-0.5" style={{ borderColor: GRAY_ACCENT }}>
                      {studentName}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-[80px] shrink-0">Enrollment No.:</span>
                    <span className="font-bold border-b flex-1 py-0.5" style={{ borderColor: GRAY_ACCENT }}>
                      {enrollmentNo}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-[80px] shrink-0">Trade / Course:</span>
                    <span className="font-bold border-b flex-1 py-0.5" style={{ borderColor: GRAY_ACCENT }}>
                      {trade}
                    </span>
                  </div>
                  <div className="text-center mt-2 text-[8px] italic text-gray-500">
                    <p>Please carry this ID card at all times within campus.</p>
                  </div>
                </div>
              </div>

              {/* FOOTER SECTION (Address) */}
              <div
                className="text-center text-white py-1.5 px-2 font-semibold text-[9px] opacity-90 print:bg-[#004085]"
                style={{ backgroundColor: PRIMARY, color: TEXT_LIGHT }}
              >
                {address}
              </div>
            </div>
          </div>

          {/* Print-specific styles */}
          <style jsx>{`
            @media print {
              @page { size: 54mm 85.6mm portrait; margin: 5mm; }
              body * {
                visibility: hidden;
              }
              .id-card-container,
              .id-card-container * {
                visibility: visible;
              }
              .id-card-container {
                position: absolute;
                top: 0;
                left: 0;
                width: 54mm;
                height: 85.6mm;
                margin: 0;
              }
              .print\\:bg-\\[\\#004085\\] {
                background-color: #004085 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .print\\:bg-\\[\\#f4511e\\] {
                background-color: #f4511e !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .print-hidden {
                display: none !important;
              }
            }
          `}</style>
        </div>
      )}
    </>
  )
}